import React, { useState, useEffect } from 'react'
import { VITE_API_BASE as API_BASE } from '../config/apiConfig'
import { Card, Row, Col, Statistic, Table, Progress } from 'antd'

const AnalyticsDashboard = () => {
  const [overview, setOverview] = useState(null)
  const [salesTrend, setSalesTrend] = useState([])
  const [userTrend, setUserTrend] = useState([])
  const [courseStats, setCourseStats] = useState([])
  const [productRanking, setProductRanking] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [overviewRes, salesRes, userRes, courseRes, productRes] = await Promise.all([
        fetch(`${API_BASE}/analytics/overview`),
        fetch(`${API_BASE}/analytics/sales-trend?days=7`),
        fetch(`${API_BASE}/analytics/user-trend?days=7`),
        fetch(`${API_BASE}/analytics/course-stats`),
        fetch(`${API_BASE}/analytics/product-ranking?limit=10`)
      ])

      const [overviewData, salesData, userData, courseData, productData] = await Promise.all([
        overviewRes.json(),
        salesRes.json(),
        userRes.json(),
        courseRes.json(),
        productRes.json()
      ])

      if (overviewData.success) setOverview(overviewData.data)
      if (salesData.success) setSalesTrend(salesData.data)
      if (userData.success) setUserTrend(userData.data)
      if (courseData.success) {
        setCourseStats(courseData.data.completionRates || [])
      }
      if (productData.success) setProductRanking(productData.data)

    } catch (err) {
      console.error('加载数据失败:', err)
    }
    setLoading(false)
  }

  const renderTrendChart = (data, valueKey, label) => {
    const max = Math.max(...(data || []).map(d => d[valueKey]), 1)
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 120 }}>
        {data.map((item, index) => (
          <div key={index} style={{ flex: 1, textAlign: 'center' }}>
            <div 
              style={{ 
                height: (item[valueKey] / max * 100) || 2,
                background: 'linear-gradient(180deg, #667eea, #764ba2)',
                borderRadius: '4px 4px 0 0',
                minHeight: 4
              }} 
            />
            <div style={{ fontSize: 12, marginTop: 8, color: '#666' }}>
              {item[valueKey]}
            </div>
            <div style={{ fontSize: 10, color: '#999' }}>
              {item.date?.substring(5)}
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: '100vh' }}>
      <h1 style={{ marginBottom: 24 }}>📊 数据分析</h1>

      {/* 概览卡片 */}
      {overview && (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="总用户数" 
                  value={overview.userStats?.totalParents || 0}
                  suffix="家长"
                />
                <div style={{ color: '#52c41a', marginTop: 8 }}>
                  今日新增: {overview.userStats?.newParentsToday || 0}
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="孩子数量" 
                  value={overview.userStats?.totalChildren || 0}
                  suffix="人"
                />
                <div style={{ color: '#52c41a', marginTop: 8 }}>
                  今日新增: {overview.userStats?.newChildrenToday || 0}
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="总订单数" 
                  value={overview.orderStats?.totalOrders || 0}
                />
                <div style={{ color: '#faad14', marginTop: 8 }}>
                  今日订单: {overview.orderStats?.todayOrders || 0}
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="总收入" 
                  value={overview.orderStats?.totalAmount || 0}
                  prefix="¥"
                />
                <div style={{ color: '#faad14', marginTop: 8 }}>
                  今日收入: ¥{overview.orderStats?.todayAmount || 0}
                </div>
              </Card>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="总学习次数" 
                  value={overview.learningStats?.totalLearnings || 0}
                />
                <div style={{ color: '#722ed1', marginTop: 8 }}>
                  今日学习: {overview.learningStats?.todayLearnings || 0}次
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="总学习时长" 
                  value={Math.round((overview.learningStats?.totalStudyMinutes || 0) / 60)}
                  suffix="小时"
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="作品总数" 
                  value={overview.workStats?.totalWorks || 0}
                />
                <div style={{ color: '#52c41a', marginTop: 8 }}>
                  已审核: {overview.workStats?.approvedWorks || 0}
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="活动总数" 
                  value={overview.activityStats?.totalActivities || 0}
                />
                <div style={{ color: '#667eea', marginTop: 8 }}>
                  待开展: {overview.activityStats?.upcomingActivities || 0}
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* 趋势图表 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card title="📈 销售趋势（7天）">
            {salesTrend.length > 0 && (
              <div>
                {renderTrendChart(salesTrend, 'amount', '销售额')}
                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
                  <span>销售额趋势</span>
                  <span>总订单: {salesTrend.reduce((sum, d) => sum + d.orders, 0)}</span>
                </div>
              </div>
            )}
          </Card>
        </Col>
        <Col span={12}>
          <Card title="👥 用户增长（7天）">
            {userTrend.length > 0 && (
              <div>
                <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: '#999' }}>新增家长</div>
                    <div style={{ fontSize: 24, color: '#667eea', fontWeight: 'bold' }}>
                      {userTrend.reduce((sum, d) => sum + d.newParents, 0)}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: '#999' }}>新增孩子</div>
                    <div style={{ fontSize: 24, color: '#52c41a', fontWeight: 'bold' }}>
                      {userTrend.reduce((sum, d) => sum + d.newChildren, 0)}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 80 }}>
                  {userTrend.map((item, index) => {
                    const max = Math.max(...userTrend.map(d => d.newParents + d.newChildren), 1)
                    const height = ((item.newParents + item.newChildren) / max * 80) || 2
                    return (
                      <div key={index} style={{ flex: 1, textAlign: 'center' }}>
                        <div 
                          style={{ 
                            height, 
                            background: '#667eea',
                            borderRadius: 4
                          }} 
                        />
                        <div style={{ fontSize: 10, marginTop: 4, color: '#999' }}>
                          {item.date?.substring(5)}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* 课程完成率 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card title="📚 课程完成率">
            {courseStats.map((course, index) => (
              <div key={index} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>
                    {course.courseType === 'literacy' && '📖 识字课程'}
                    {course.courseType === 'reading' && '📚 阅读课程'}
                    {course.courseType === 'ai' && '🤖 AI创作'}
                  </span>
                  <span>{course.completionRate}%</span>
                </div>
                <Progress 
                  percent={course.completionRate} 
                  strokeColor={{
                    '0%': '#667eea',
                    '100%': '#764ba2',
                  }}
                />
                <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                  已完成 {course.completed} / 进行中 {course.inProgress}
                </div>
              </div>
            ))}
          </Card>
        </Col>
        <Col span={12}>
          <Card title="🏆 产品销售排行">
            <Table
              dataSource={productRanking.map((p, i) => ({ ...p, key: i }))}
              columns={[
                { title: '#', dataIndex: 'key', render: (v) => v + 1 },
                { title: '产品', dataIndex: 'product_name' },
                { title: '分类', dataIndex: 'category' },
                { title: '订单数', dataIndex: 'order_count' },
                { 
                  title: '销售额', 
                  dataIndex: 'total_amount',
                  render: (v) => `¥${v || 0}`
                }
              ]}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default AnalyticsDashboard
